import { baseRequest } from '../utils/base-request';

/**
 * Codi AI — natural-language analytics over BigQuery.
 *
 * The browser talks to the MedHaul backend (`/codi-analytics/*`), which proxies
 * to the cod-mhadmin Conversational Analytics service. Conversations are
 * persisted upstream, so the UI keeps a history list and can reopen any past
 * thread; the active thread stays context-aware across follow-ups.
 */

const types = {
  CODI_ANALYTICS_ASK_REQUEST: 'CODI_ANALYTICS_ASK_REQUEST',
  CODI_ANALYTICS_ASK_SUCCESS: 'CODI_ANALYTICS_ASK_SUCCESS',
  CODI_ANALYTICS_ASK_ERROR: 'CODI_ANALYTICS_ASK_ERROR',
  CODI_ANALYTICS_AWAIT_ANSWER: 'CODI_ANALYTICS_AWAIT_ANSWER',
  CODI_ANALYTICS_SET_CONVERSATION: 'CODI_ANALYTICS_SET_CONVERSATION',
  CODI_ANALYTICS_SET_CONVERSATIONS: 'CODI_ANALYTICS_SET_CONVERSATIONS',
  CODI_ANALYTICS_ADD_CONVERSATION: 'CODI_ANALYTICS_ADD_CONVERSATION',
  CODI_ANALYTICS_CONVERSATIONS_LOADING: 'CODI_ANALYTICS_CONVERSATIONS_LOADING',
  CODI_ANALYTICS_THREAD_LOADING: 'CODI_ANALYTICS_THREAD_LOADING',
  CODI_ANALYTICS_LOAD_THREAD: 'CODI_ANALYTICS_LOAD_THREAD',
  CODI_ANALYTICS_RESET: 'CODI_ANALYTICS_RESET',
};

const initialState = {
  loading: false,
  error: null,
  conversationId: null,
  // [{ id, title, createdAt, updatedAt }] — the history list.
  conversations: [],
  conversationsLoading: false,
  // True while a past thread is being fetched after clicking it in the rail.
  threadLoading: false,
  // [{ id, role: 'user' | 'assistant', text, result, error, latencyMs }]
  messages: [],
};

export const askCodiAnalyticsRequest = (question) => ({
  type: types.CODI_ANALYTICS_ASK_REQUEST,
  payload: question,
});

export const askCodiAnalyticsSuccess = (message) => ({
  type: types.CODI_ANALYTICS_ASK_SUCCESS,
  payload: message,
});

export const askCodiAnalyticsError = (error) => ({
  type: types.CODI_ANALYTICS_ASK_ERROR,
  payload: error,
});

// Show the thinking state for a question asked earlier — a thread reopened
// while its answer is still being generated.
export const awaitCodiAnswer = () => ({
  type: types.CODI_ANALYTICS_AWAIT_ANSWER,
});

export const setCodiConversation = (conversationId) => ({
  type: types.CODI_ANALYTICS_SET_CONVERSATION,
  payload: conversationId,
});

export const setCodiConversations = (conversations) => ({
  type: types.CODI_ANALYTICS_SET_CONVERSATIONS,
  payload: conversations,
});

// Prepend a freshly created thread to the history rail so it shows up the
// instant the chat starts — before the first answer comes back.
export const addCodiConversation = (conversation) => ({
  type: types.CODI_ANALYTICS_ADD_CONVERSATION,
  payload: conversation,
});

export const setCodiConversationsLoading = (loading) => ({
  type: types.CODI_ANALYTICS_CONVERSATIONS_LOADING,
  payload: loading,
});

// `conversationId` is optional; when passed it highlights the target row
// immediately so the switch feels responsive while the thread loads.
export const setCodiThreadLoading = (loading, conversationId) => ({
  type: types.CODI_ANALYTICS_THREAD_LOADING,
  payload: { loading, conversationId },
});

export const loadCodiThread = (conversationId, messages) => ({
  type: types.CODI_ANALYTICS_LOAD_THREAD,
  payload: { conversationId, messages },
});

export const resetCodiAnalytics = () => ({
  type: types.CODI_ANALYTICS_RESET,
});

let pendingKey = 0;

const codiAnalyticsReducer = (state = initialState, action) => {
  switch (action.type) {
    case types.CODI_ANALYTICS_SET_CONVERSATION:
      return { ...state, conversationId: action.payload };

    case types.CODI_ANALYTICS_SET_CONVERSATIONS:
      return { ...state, conversations: action.payload };

    case types.CODI_ANALYTICS_ADD_CONVERSATION: {
      const conv = action.payload;
      if (!conv?.id) return state;
      // De-dupe by id (a later silent refresh may bring the same row) and keep
      // the newest on top.
      const rest = state.conversations.filter((c) => c.id !== conv.id);
      return { ...state, conversations: [conv, ...rest] };
    }

    case types.CODI_ANALYTICS_CONVERSATIONS_LOADING:
      return { ...state, conversationsLoading: action.payload };

    case types.CODI_ANALYTICS_THREAD_LOADING:
      return {
        ...state,
        threadLoading: action.payload.loading,
        ...(action.payload.conversationId !== undefined
          ? { conversationId: action.payload.conversationId }
          : {}),
      };

    case types.CODI_ANALYTICS_LOAD_THREAD:
      return {
        ...state,
        loading: false,
        threadLoading: false,
        error: null,
        conversationId: action.payload.conversationId,
        messages: action.payload.messages,
      };

    case types.CODI_ANALYTICS_ASK_REQUEST:
      return {
        ...state,
        loading: true,
        error: null,
        messages: [
          ...state.messages,
          { id: `user-${(pendingKey += 1)}`, role: 'user', text: action.payload },
        ],
      };

    case types.CODI_ANALYTICS_AWAIT_ANSWER:
      return { ...state, loading: true, error: null };

    case types.CODI_ANALYTICS_ASK_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        messages: [...state.messages, action.payload],
      };

    case types.CODI_ANALYTICS_ASK_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload,
        messages: [
          ...state.messages,
          {
            id: `assistant-error-${(pendingKey += 1)}`,
            role: 'assistant',
            text: '',
            error: action.payload,
          },
        ],
      };

    case types.CODI_ANALYTICS_RESET:
      // Clear the active thread but keep the history list so past chats stay
      // reachable after starting a new one.
      return {
        ...initialState,
        conversations: state.conversations,
        conversationsLoading: state.conversationsLoading,
      };

    // Identity changed (login-as HO, leave dashboard, logout). Drop MH
    // threads so the next Codi visit loads only this session's org scope.
    case 'LOGIN_AS_DIFFERENT_ROLE_SUCCESS':
    case 'LEAVE_VIEW_DASHBOARD_MODE_SUCCESS':
    case 'LOGOUT':
      return { ...initialState };

    default:
      return state;
  }
};

// Map a persisted upstream message onto the shape the UI renders.
const mapStoredMessage = (msg) => {
  if (msg.role === 'user') {
    return { id: msg.id, role: 'user', text: msg.text };
  }
  return {
    id: msg.id,
    role: 'assistant',
    text: msg.text || msg.result?.text || '',
    result: msg.result || null,
    error: msg.error || null,
    latencyMs: msg.latencyMs ?? msg.latency_ms ?? null,
  };
};

// --- polling for the answer -------------------------------------------------
const POLL_FIRST_DELAY_MS = 700;
const POLL_INTERVAL_MS = 2000;
const POLL_MAX_INTERVAL_MS = 5000;
const POLL_TIMEOUT_MS = 20 * 60 * 1000;
const POLL_MAX_CONSECUTIVE_ERRORS = 15;

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const whenVisible = () => {
  if (typeof document === 'undefined' || !document.hidden) return Promise.resolve();
  return new Promise((resolve) => {
    const onVisible = () => {
      if (document.hidden) return;
      document.removeEventListener('visibilitychange', onVisible);
      resolve();
    };
    document.addEventListener('visibilitychange', onVisible);
  });
};

const readMessage = (conversationId, messageId) =>
  baseRequest(
    'GET',
    `/codi-analytics/conversations/${conversationId}/messages/${messageId}`,
    undefined,
    {},
    undefined,
    undefined,
    { silent: true },
  );

// Poll one message until its status leaves `pending`, then resolve with it.
const pollAssistantMessage = async (conversationId, messageId, timeoutMs) => {
  const deadline = Date.now() + (timeoutMs || POLL_TIMEOUT_MS);
  let interval = POLL_FIRST_DELAY_MS;
  let consecutiveErrors = 0;

  while (Date.now() < deadline) {
    await sleep(interval);
    await whenVisible();

    try {
      const { data } = await readMessage(conversationId, messageId);
      consecutiveErrors = 0;
      // No status at all means an older service that answers synchronously —
      // treat the row as settled rather than polling it forever.
      if (data?.status !== 'pending') return data;
      interval = Math.min(
        Math.max(Math.round(interval * 1.5), POLL_INTERVAL_MS),
        POLL_MAX_INTERVAL_MS,
      );
    } catch (error) {
      if (error?.response?.status === 404) throw error;
      consecutiveErrors += 1;
      if (consecutiveErrors >= POLL_MAX_CONSECUTIVE_ERRORS) throw error;
      interval = Math.min(
        Math.max(interval, POLL_INTERVAL_MS) * 2,
        POLL_MAX_INTERVAL_MS * 2,
      );
    }
  }

  throw new Error(
    'Codi AI is still working on this one. The answer is saved to this chat — reopen it in a moment.',
  );
};


const collectAnswer =
  (conversationId, messageId, timeoutMs) => async (dispatch, getState) => {
    const stillOnThread = () => getState()?.codiAnalytics?.conversationId === conversationId;

    try {
      const assistant = await pollAssistantMessage(conversationId, messageId, timeoutMs);
      if (!stillOnThread()) return null;

      const message = mapStoredMessage(assistant);
      dispatch(askCodiAnalyticsSuccess(message));
      // Re-sort the history list by most-recent now that the turn is done.
      dispatch(loadCodiConversations({ silent: true }));
      return message;
    } catch (error) {
      if (!stillOnThread()) return null;

      if (error?.response?.status !== 404) {
        try {
          const { data } = await readMessage(conversationId, messageId);
          if (data && data.status !== 'pending') {
            if (!stillOnThread()) return null;
            const recovered = mapStoredMessage(data);
            dispatch(askCodiAnalyticsSuccess(recovered));
            dispatch(loadCodiConversations({ silent: true }));
            return recovered;
          }
        } catch {
          console.error("Failed to get an answer from Codi AI")
        }
      }

      if (!stillOnThread()) return null;
      dispatch(
        askCodiAnalyticsError(
          error?.response?.data?.message ||
            error?.message ||
            'Failed to get an answer from Codi AI',
        ),
      );
      dispatch(loadCodiConversations({ silent: true }));
      return null;
    }
  };

const ensureConversation = async (getState, dispatch, firstQuestion) => {
  const existing = getState()?.codiAnalytics?.conversationId;
  if (existing) return existing;

  const { data } = await baseRequest('POST', '/codi-analytics/conversations', {
    title: (firstQuestion || 'New chat').slice(0, 60),
  });

  const conversationId = data?.id;
  if (!conversationId) {
    throw new Error('Could not start an analytics conversation.');
  }

  dispatch(setCodiConversation(conversationId));
  // Surface the new thread in the history rail immediately, while the first
  // answer is still being generated. The upstream response is the full
  // conversation row ({ id, title, createdAt, updatedAt }).
  dispatch(addCodiConversation(data));
  return conversationId;
};

// Fetch the history list. Pass { silent: true } to refresh without flipping the
// loading flag (e.g. right after sending a message).
export const loadCodiConversations = ({ silent = false } = {}) => async (dispatch) => {
  if (!silent) dispatch(setCodiConversationsLoading(true));
  try {
    const { data } = await baseRequest('GET', '/codi-analytics/conversations');
    dispatch(setCodiConversations(data?.conversations || []));
  } catch {
    /* the request interceptor surfaces the error; keep the list as-is */
  } finally {
    if (!silent) dispatch(setCodiConversationsLoading(false));
  }
};

// Open a past conversation and replay its messages into the thread.
export const openCodiConversation = (conversationId) => async (dispatch, getState) => {
  if (!conversationId) return;
  const { conversationId: current, loading, threadLoading } = getState()?.codiAnalytics || {};
  if (loading || threadLoading || current === conversationId) return;

  // Highlight the row and show the loading state straight away.
  dispatch(setCodiThreadLoading(true, conversationId));
  try {
    const { data } = await baseRequest(
      'GET',
      `/codi-analytics/conversations/${conversationId}`,
    );

    const stored = data?.messages || [];
    const pending = stored.find((msg) => msg.status === 'pending');
    const messages = stored.filter((msg) => msg.status !== 'pending').map(mapStoredMessage);

    dispatch(loadCodiThread(conversationId, messages));

    if (pending) {
      dispatch(awaitCodiAnswer());
      dispatch(collectAnswer(conversationId, pending.id));
    }
  } catch {
    // Surfaced by the interceptor; just drop the loading state.
    dispatch(setCodiThreadLoading(false));
  }
};

// Ask the agent a question. Resolves with the assistant message.
export const askCodiAnalytics = (question) => async (dispatch, getState) => {
  const prompt = (question || '').trim();
  if (!prompt) return null;

  dispatch(askCodiAnalyticsRequest(prompt));
  let askedIn = null;

  try {
    const conversationId = await ensureConversation(getState, dispatch, prompt);
    askedIn = conversationId;

    // Step 1 — queue the question. Comes back as soon as it is accepted, with a
    // `pending` assistant turn to poll.
    const { data } = await baseRequest(
      'POST',
      `/codi-analytics/conversations/${conversationId}/messages`,
      { text: prompt },
    );

    const queued = data?.assistant || {};
    if (!queued.id) {
      throw new Error('Codi AI did not accept the question. Please try again.');
    }

    // The first turn sets the thread title upstream — surface it right away
    // rather than after the (potentially minutes-long) answer.
    dispatch(loadCodiConversations({ silent: true }));

    // An older service answered inline: nothing to wait for.
    if (queued.status !== 'pending') {
      const message = mapStoredMessage(queued);
      dispatch(askCodiAnalyticsSuccess(message));
      return message;
    }

    return dispatch(collectAnswer(conversationId, queued.id, data?.poll_timeout_ms));
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.message ||
      'Failed to get an answer from Codi AI';
    // Never report this turn's failure into a thread the user has moved on to.
    if (askedIn !== null && getState()?.codiAnalytics?.conversationId !== askedIn) {
      return null;
    }
    dispatch(askCodiAnalyticsError(message));
    dispatch(loadCodiConversations({ silent: true }));
    return null;
  }
};

// Rename a thread, then refresh the history list.
export const renameCodiConversation = (conversationId, title) => async (dispatch) => {
  const next = (title || '').trim();
  if (!conversationId || !next) return;

  try {
    await baseRequest('PATCH', `/codi-analytics/conversations/${conversationId}`, {
      title: next.slice(0, 200),
    });
    dispatch(loadCodiConversations({ silent: true }));
  } catch {
    /* ignore */
  }
};

// Delete a thread. If it is the one on screen, drop back to a fresh chat.
export const deleteCodiConversation = (conversationId) => async (dispatch, getState) => {
  if (!conversationId) return;
  const isActive = getState()?.codiAnalytics?.conversationId === conversationId;

  try {
    await baseRequest('DELETE', `/codi-analytics/conversations/${conversationId}`);
  } catch {
    /* a stale row upstream must not block the UI */
  }

  if (isActive) dispatch(resetCodiAnalytics());
  dispatch(loadCodiConversations({ silent: true }));
};

// Drop the current thread so the next question starts fresh. Previous
// conversations stay in the history list.
export const startNewCodiAnalyticsChat = () => (dispatch) => {
  dispatch(resetCodiAnalytics());
};

export default codiAnalyticsReducer;
